/* ContextStack — pattern générique de navigation contextuelle dans l'ontologie NOAM :
 * Objet → Relation → Objet → Preuve. Panneaux empilables avec fil d'Ariane,
 * retour niveau par niveau, fermeture totale, état parent préservé (overlay).
 * Réutilisable pour toute chaîne relationnelle (GRC, fournisseurs, deals, processus).
 *
 * API : <ContextStack stack={[{id, crumb, title, subtitle, accent, render}]}
 *         rootLabel="Matrice" onPop={(n)=>...} onClose={()=>...} />
 * - stack[i].render(pushPanel) reçoit une fonction pour empiler le niveau suivant. */
import { ArrowLeft, X, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function ContextStack({ stack, rootLabel, onPop, onClose, push }) {
  const { t } = useTranslation();
  if (!stack || stack.length === 0) return null;
  const root = rootLabel || t("saas.axp.ctx_root");
  const top = stack[stack.length - 1];
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={onClose} data-testid="context-stack">
      <div
        className="w-full sm:max-w-2xl h-full flex flex-col bg-[#0D0D0F] border-l border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Fil d'Ariane — chaque niveau reste cliquable, le contexte n'est jamais perdu */}
        <div className="flex items-center gap-1 px-4 py-3 border-b border-white/[0.07] overflow-x-auto shrink-0"
          data-testid="context-stack-breadcrumb">
          <button onClick={() => (stack.length > 1 ? onPop(stack.length - 1) : onClose())}
            className="p-1.5 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 transition-colors shrink-0"
            data-testid="context-stack-back" title={t("saas.axp.ctx_back")}>
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button onClick={onClose} data-testid="context-stack-root"
            className="text-[11px] text-zinc-500 hover:text-white whitespace-nowrap transition-colors">
            {root}
          </button>
          {stack.map((p, i) => (
            <span key={p.id} className="flex items-center gap-1 shrink-0">
              <ChevronRight className="w-3 h-3 text-zinc-600" />
              <button onClick={() => onPop(i + 1)} disabled={i === stack.length - 1}
                data-testid={`context-stack-crumb-${i}`}
                className={`text-[11px] whitespace-nowrap px-1.5 py-0.5 rounded transition-colors ${
                  i === stack.length - 1 ? "text-white bg-white/10" : "text-zinc-400 hover:text-white"
                }`}>
                {p.crumb}
              </button>
            </span>
          ))}
          <div className="flex-1" />
          <button onClick={onClose} className="p-1.5 rounded-full text-zinc-500 hover:text-white hover:bg-white/10 transition-colors shrink-0"
            data-testid="context-stack-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Panneau courant */}
        <div className="flex-1 overflow-y-auto p-5" data-testid={`context-panel-${top.id}`}>
          {top.title && (
            <div className="mb-4">
              {top.badge}
              <h3 className="noam-display text-lg font-bold mt-1.5">{top.title}</h3>
              {top.subtitle && <div className="text-[11px] text-zinc-400 mt-1">{top.subtitle}</div>}
            </div>
          )}
          {top.render(push)}
        </div>
      </div>
    </div>
  );
}
